/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './Callout.module.scss';

export type CalloutItem = {
  key: React.Key;
  sourceElement: HTMLElement;
  targetElement: HTMLElement;
  xOffset?: number;
  yOffset?: number;
  targetXOffset?: number;
  sourceVerticalAnchor?: 'bottom' | 'middle';
  treatSourceAsInlineAction?: boolean;
  reverseHorizontal?: boolean;
};

type CalloutProps = {
  items: CalloutItem[];
  className?: string;
  refreshEventName?: string;
  refreshKey?: unknown;
  isCurved?: boolean;
  targetContainerSelector?: string;
  portalContainer?: Element | null;
  onMissingElement?: () => void;
};

type CalloutRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

type CalloutGeometryOptions = {
  xOffset?: number;
  yOffset?: number;
  targetXOffset?: number;
  sourceVerticalAnchor?: 'bottom' | 'middle';
  isCurved?: boolean;
  treatSourceAsInlineAction?: boolean;
  reverseHorizontal?: boolean;
};

export function buildSingleCalloutGeometry(
  sourceRect: CalloutRect,
  sourceAnchorRect: CalloutRect,
  targetRect: CalloutRect,
  targetContainerRect: CalloutRect,
  {
    xOffset,
    yOffset,
    targetXOffset,
    sourceVerticalAnchor = 'bottom',
    isCurved = true,
    treatSourceAsInlineAction = false,
    reverseHorizontal = false,
  }: CalloutGeometryOptions = {}
) {
  let x = reverseHorizontal ? sourceAnchorRect.left : sourceAnchorRect.left;
  let y = sourceAnchorRect.top;
  if (xOffset) x += xOffset;
  if (yOffset) y += yOffset;

  const x2 = reverseHorizontal ? x : x + sourceAnchorRect.width;
  const y2 =
    sourceVerticalAnchor === 'middle' || reverseHorizontal
      ? y + sourceAnchorRect.height / 2
      : y + sourceAnchorRect.height + 3;
  let y3 = targetRect.top + targetRect.height / 2;
  if (y3 < targetContainerRect.top) {
    // The middle is out the top of the rectangle.  Is any of the rect still visible?
    if (targetRect.top + targetRect.height - 20 > targetContainerRect.top) {
      // yes, so snap to the container edge
      y3 = targetContainerRect.top;
    } else {
      //So, so swoop off into the void, aiming for the bottom of the rect
      y3 = targetRect.top + targetRect.height - 20; // Bottom of the target rect
    }
  } else if (y3 > targetContainerRect.top + targetContainerRect.height) {
    // The middle is out the bottom of the rectangle.  Is any of the rect still visible?
    if (targetRect.top + 20 < targetContainerRect.top + targetContainerRect.height) {
      // yes, so snap to the container edge
      y3 = targetContainerRect.top + targetContainerRect.height;
    } else {
      //So, so swoop off into the void, aiming for the top of the rect
      y3 = targetRect.top + 20; // Top of the target rect
    }
  }
  let x3 = reverseHorizontal
    ? Math.min(targetContainerRect.left + targetContainerRect.width, targetRect.left + targetRect.width) + 6
    : Math.max(targetContainerRect.left, targetRect.left) - 6;
  if (targetXOffset) {
    x3 += reverseHorizontal ? -targetXOffset : targetXOffset;
  }
  // The middle is out the bottom of the rectangle.  Is any of the rect still visible?
  const showSourceCircle = treatSourceAsInlineAction || sourceRect.width === 0 || sourceRect.height === 0;

  return {
    showSourceCircle,
    sourceCircle: { cx: x2, cy: reverseHorizontal ? y2 : y2 - 11, r: 11 },
    connectorLine: reverseHorizontal
      ? { x1: x3 - 10, y1: y3, x2: x3, y2: y3 }
      : { x1: x3, y1: y3, x2: x3 + 10, y2: y3 },
    strokeLength: Math.sqrt((x3 - x2) ** 2 + (y3 - y2) ** 2),
    mainLine: isCurved
      ? {
          type: 'path' as const,
          d: (() => {
            const distance = Math.abs(x3 - x2);
            const controlPointOffset = Math.min(1000, distance / 2);
            if (reverseHorizontal) {
              return `M${x2} ${y2} C${x2 - controlPointOffset} ${y2} ${x3 + controlPointOffset} ${y3} ${x3} ${y3}`;
            }
            return `M${x2} ${y2} C${x2 + controlPointOffset} ${y2} ${x3 - controlPointOffset} ${y3} ${x3} ${y3}`;
          })(),
        }
      : {
          type: 'line' as const,
          x1: x2,
          y1: y2,
          x2: x3,
          y2: y3,
        },
  };
}

export function Callout({
  items,
  className,
  refreshEventName,
  refreshKey,
  isCurved = true,
  targetContainerSelector,
  portalContainer,
  onMissingElement,
}: CalloutProps) {
  return (
    <>
      {items.map((item) => (
        <SingleCallout
          key={item.key}
          sourceElement={item.sourceElement}
          targetElement={item.targetElement}
          xOffset={item.xOffset}
          yOffset={item.yOffset}
          targetXOffset={item.targetXOffset}
          sourceVerticalAnchor={item.sourceVerticalAnchor}
          treatSourceAsInlineAction={item.treatSourceAsInlineAction}
          reverseHorizontal={item.reverseHorizontal}
          className={className}
          refreshEventName={refreshEventName}
          refreshKey={refreshKey}
          isCurved={isCurved}
          targetContainerSelector={targetContainerSelector}
          portalContainer={portalContainer}
          onMissingElement={items.length === 1 ? onMissingElement : undefined}
        />
      ))}
    </>
  );
}

type SingleCalloutProps = Omit<CalloutItem, 'key'> & {
  className?: string;
  refreshEventName?: string;
  refreshKey?: unknown;
  isCurved?: boolean;
  targetContainerSelector?: string;
  portalContainer?: Element | null;
  onMissingElement?: () => void;
};

export function SingleCallout({
  sourceElement,
  targetElement, // This is the element in the sidebar, for fhir widgets
  xOffset,
  yOffset,
  targetXOffset,
  sourceVerticalAnchor,
  className,
  refreshEventName,
  refreshKey,
  isCurved = true,
  targetContainerSelector,
  portalContainer,
  treatSourceAsInlineAction,
  reverseHorizontal,
  onMissingElement,
}: SingleCalloutProps) {
  const [refreshCounter, setRefreshCounter] = useState(0);
  const isInDocument = document.contains(sourceElement) && document.contains(targetElement);
  const hasUsableGeometry = useMemo(() => {
    if (!isInDocument) {
      return false;
    }
    const sourceRects = sourceElement.getClientRects();
    const targetRects = targetElement.getClientRects();
    const sourceRect = sourceRects[0] || sourceElement.getBoundingClientRect();
    const targetRect = targetRects[0] || targetElement.getBoundingClientRect();

    return !(
      (sourceRects.length === 0 && sourceRect.width === 0 && sourceRect.height === 0) ||
      (targetRects.length === 0 && targetRect.width === 0 && targetRect.height === 0)
    );
  }, [isInDocument, sourceElement, targetElement]);

  useEffect(() => {
    if (!refreshEventName) return undefined;

    const listener = () => {
      setRefreshCounter((prev) => prev + 1);
    };
    window.addEventListener(refreshEventName, listener);
    return () => {
      window.removeEventListener(refreshEventName, listener);
    };
  }, [refreshEventName]);

  useEffect(() => {
    if (!isInDocument || !hasUsableGeometry) {
      onMissingElement?.();
    }
  }, [isInDocument, hasUsableGeometry, onMissingElement]);

  const line = useMemo(() => {
    void refreshCounter;
    void refreshKey;

    const sourceRect = sourceElement.getBoundingClientRect();
    const sourceAnchorRect = sourceElement.getClientRects()[0] || sourceRect;
    const nextTargetRect = targetElement.getBoundingClientRect();
    const nextTargetContainerRect =
      (targetContainerSelector ? targetElement.closest(targetContainerSelector)?.getBoundingClientRect() : undefined) ||
      nextTargetRect;

    const geometry = buildSingleCalloutGeometry(sourceRect, sourceAnchorRect, nextTargetRect, nextTargetContainerRect, {
      xOffset,
      yOffset,
      targetXOffset,
      sourceVerticalAnchor,
      isCurved,
      treatSourceAsInlineAction,
      reverseHorizontal,
    });

    const components: ReactNode[] = [];
    if (geometry.showSourceCircle) {
      components.push(
        <circle
          key="source-circle"
          cx={geometry.sourceCircle.cx}
          cy={geometry.sourceCircle.cy}
          r={geometry.sourceCircle.r}
          stroke="currentColor"
          fill="transparent"
          strokeWidth={3}
        />
      );
    }

    if (geometry.mainLine.type === 'line') {
      components.push(
        <line
          key="main-line"
          className={styles.line2}
          x1={geometry.mainLine.x1}
          y1={geometry.mainLine.y1}
          x2={geometry.mainLine.x2}
          y2={geometry.mainLine.y2}
          stroke="currentColor"
        />
      );
    } else {
      components.push(
        <path
          key="main-line"
          className={styles.line2}
          d={geometry.mainLine.d}
          stroke="currentColor"
          fill="transparent"
        />
      );
    }

    components.push(
      <line
        key="connector-line"
        className={styles.line3}
        x1={geometry.connectorLine.x1}
        y1={geometry.connectorLine.y1}
        x2={geometry.connectorLine.x2}
        y2={geometry.connectorLine.y2}
        stroke="currentColor"
      />
    );

    return { components, length: geometry.strokeLength };
  }, [
    sourceElement,
    targetElement,
    xOffset,
    yOffset,
    targetXOffset,
    sourceVerticalAnchor,
    isCurved,
    treatSourceAsInlineAction,
    targetContainerSelector,
    refreshCounter,
    refreshKey,
    reverseHorizontal,
  ]);

  if (!isInDocument || !hasUsableGeometry) {
    return null;
  }

  const callout = (
    <svg
      className={`${styles.container} ${className || ''}`.trim()}
      style={{ '--stroke-length': line.length } as React.CSSProperties}
    >
      {line.components}
    </svg>
  );

  return portalContainer ? createPortal(callout, portalContainer) : callout;
}
